/**
 * Vignobles › Appellations (/vignobles/mes-appellations) — appellations RÉFÉRENCÉES dans la cave,
 * sur le même principe que « Domaines » : nb de domaines, cuvées et bouteilles en cave.
 * Un clic ouvre un volet flottant (droite) listant les domaines de la carte pour cette appellation ;
 * un clic sur un domaine ouvre sa fiche.
 */
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, ChevronRight, MapPin } from 'lucide-react';
import { vignoblesApi } from '../../services/api';
import { Spinner, EmptyState, PageTitle } from '../../components/ui/kit';
import { Drawer } from '../../components/ui/Drawer';

const norm = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export default function CaveAppellations() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const { data: rows = [], isLoading } = useQuery({ queryKey: ['cave-appellations'], queryFn: () => vignoblesApi.caveAppellations() });

  const filtered = useMemo(() => {
    const t = norm(q.trim());
    return t ? rows.filter((r) => norm(r.appellation).includes(t)) : rows;
  }, [rows, q]);

  const totals = useMemo(() => ({
    appellations: rows.length,
    bottles: rows.reduce((s, r) => s + r.bottles, 0),
  }), [rows]);

  return (
    <div className="p-4 md:p-6 max-w-4xl">
      <PageTitle kicker="Vignobles" title="Appellations de la cave" />
      <p className="text-[13px] text-n600 mt-1 mb-4">{totals.appellations} appellation(s) · {totals.bottles} bouteilles en cave</p>

      <div className="relative max-w-md mb-4">
        <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-n500" />
        <input className="vd-input h-9 pl-8" placeholder="Rechercher une appellation…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="cave-appellation-search" />
      </div>

      {isLoading ? <Spinner /> : filtered.length === 0 ? (
        <EmptyState title="Aucune appellation" description="Aucune appellation référencée dans la cave." testId="cave-appellations-empty" />
      ) : (
        <div className="vd-card overflow-hidden">
          <table className="w-full text-[13.5px]" data-testid="cave-appellations-table">
            <thead><tr className="text-left kicker">
              <th className="py-2.5 px-3">Appellation</th>
              <th className="py-2.5 px-3 text-right">Domaines</th>
              <th className="py-2.5 px-3 text-right">Cuvées</th>
              <th className="py-2.5 px-3 text-right">En cave</th>
              <th className="py-2.5 px-3 w-8"></th>
            </tr></thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.appellation} className="hair-t hover:bg-surface/60 cursor-pointer" onClick={() => setSelected(r)} data-testid={`cave-appellation-row-${r.appellation}`}>
                  <td className="py-2.5 px-3 font-title text-lg">{r.appellation}</td>
                  <td className="py-2.5 px-3 text-right nums text-n600">{r.domains_count}</td>
                  <td className="py-2.5 px-3 text-right nums text-n600">{r.references_count}</td>
                  <td className="py-2.5 px-3 text-right nums">{r.bottles} btl</td>
                  <td className="py-2.5 px-3 text-n500"><ChevronRight className="w-4 h-4" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Drawer open={!!selected} onClose={() => setSelected(null)} title={selected?.appellation || ''} testId="cave-appellation-drawer" width="max-w-md">
        {selected && (
          <>
            <p className="text-[13px] text-n600 mb-3">{selected.domains_count} domaine(s) · {selected.bottles} bouteilles en cave</p>
            <p className="kicker mb-2">Domaines de la carte</p>
            <div className="space-y-1.5">
              {selected.domains.map((d) => (
                <button key={d.id} onClick={() => navigate(`/vignobles/domaine/${d.id}`)} data-testid={`cave-appellation-domain-${d.id}`}
                  className="w-full flex items-center justify-between vd-card px-3 py-2.5 text-left hover:bg-surface transition-colors">
                  <span className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-n500" /> <span className="font-title text-base">{d.name}</span></span>
                  <span className="flex items-center gap-2 text-[13px] text-n600 nums">{d.bottles} btl <ChevronRight className="w-4 h-4 text-n500" /></span>
                </button>
              ))}
            </div>
          </>
        )}
      </Drawer>
    </div>
  );
}
